import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { Avatar } from "../primitives/Avatar";
import { useAvatarBlobUrl } from "../../helpers/useAvatarBlobUrl";
import { ME_AVATAR } from "../../helpers/url_helper";
import { useIsMobile } from "../primitives/useIsMobile";
import logoMark from "../assets/logo-sm.png";
import logoWordmark from "../assets/kronaly-dark.png";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import {
  getOvertimeRequests,
  getAbsences,
  getWorkspaces,
} from "../../helpers/kronaly_helper";
import { setAuthorization, getLoggedinUser } from "../../helpers/api_helper";

const ROLE_LABEL = {
  owner: "Propietario",
  admin: "Administrador",
  manager: "Manager",
  employee: "Empleado",
};

/**
 * Cada entrada del sidebar declara:
 *  - `module` (opcional): módulo de Capa 1. Si está off en el workspace,
 *    la entrada se oculta.
 *  - `capability` (opcional): capability de Capa 2/3. Si no se tiene
 *    (override + default rol), la entrada se oculta.
 * Sin module ni capability → siempre visible para cualquier miembro.
 *
 * Cuando se añade una sección nueva, prefiere `capability` antes que un
 * check directo a `isManager`/`isAdmin` — sólo se justifica role check
 * para entradas sin capability mapeada todavía (Reportes, Roles).
 */
function buildNav(isManager, isAdmin, can, pendingCount, isOwner) {
  // "Documentos" (gestor) y "Mis documentos" (personal) son pantallas
  // distintas mutuamente exclusivas según la capability — sólo aparece una.
  const canManageDocuments = can("documents:manage");
  const docsItem = canManageDocuments
    ? { id: "documentos", label: "Documentos", icon: "FileText", to: "/documentos", module: "documentos" }
    : { id: "mis-documentos", label: "Mis documentos", icon: "FileText", to: "/mis-documentos", module: "documentos" };

  return [
    {
      items: [
        { id: "dashboard", label: "Dashboard", icon: "LayoutDashboard", to: "/dashboard" },
      ],
    },
    {
      title: "Personal",
      items: [
        { id: "jornada", label: "Mi jornada", icon: "Clock", to: "/jornada", module: "fichaje" },
        { id: "ausencias", label: "Ausencias", icon: "CalendarDays", to: "/ausencias", module: "calendario" },
        { id: "historial", label: "Historial", icon: "List", to: "/jornada/historial", module: "fichaje" },
        { id: "mis-solicitudes", label: "Mis solicitudes", icon: "Inbox", to: "/mis-solicitudes", module: "calendario" },
        docsItem,
      ],
    },
    {
      title: "Documentación",
      items: [
        { id: "documentacion-interna", label: "Documentación interna", icon: "Library", to: "/documentacion-interna", module: "documentos_internos" },
        { id: "documentacion-config", label: "Niveles y tipos", icon: "Settings2", to: "/documentacion-interna/configuracion", module: "documentos_internos", capability: "internal_docs:manage_catalogs" },
      ],
    },
    {
      title: "RRHH",
      items: [
        // Bandeja de aprobación. Visible a cualquier aprobador (owner, manager,
        // manager de equipo) gateando por capability — el scope (todo el
        // workspace vs. solo su equipo) lo resuelve el backend.
        { id: "solicitudes", label: "Solicitudes", icon: "Inbox", to: "/solicitudes", count: pendingCount, capabilities: ["absences:approve", "overtime:approve"] },
        { id: "calendario", label: "Calendario", icon: "Calendar", to: "/calendario", module: "calendario", capability: "absences:approve" },
        { id: "rrhh-jornadas", label: "Jornadas", icon: "ClipboardList", to: "/rrhh/jornadas", module: "fichaje", capability: "time_tracking_corrections:approve" },
        // Log inmutable de auditoría. Manager o RLT (representante legal).
        { id: "auditoria-jornadas", label: "Auditoría fichaje", icon: "FileSearch", to: "/jornada/auditoria", module: "fichaje", capabilities: ["employees:manage", "time_tracking:rlt_view"] },
        { id: "excesos-jornada", label: "Excesos jornada", icon: "AlertTriangle", to: "/excesos-jornada", module: "fichaje", capability: "overtime:approve" },
        { id: "empleados", label: "Empleados", icon: "Users", to: "/empleados", module: "empleados", capability: "employees:manage" },
        { id: "equipos", label: "Equipos", icon: "UsersRound", to: "/equipos", module: "empleados", capability: "employees:manage" },
        { id: "contratos", label: "Contratos", icon: "Briefcase", to: "/contratos", module: "contratos", capability: "contracts:manage" },
      ],
    },
    {
      title: "Reglas",
      items: [
        { id: "horarios", label: "Horarios", icon: "Clock", to: "/horarios", module: "contratos", capability: "contracts:manage" },
        { id: "festivos", label: "Festivos", icon: "Flag", to: "/festivos", module: "contratos", capability: "contracts:manage" },
      ],
    },
    {
      title: "Aprendizaje",
      // Módulo de Formación — UI-only por ahora. Sin gating de módulo/capability
      // hasta que exista el backend; entonces se añadirá `module: "formacion"`
      // y las capabilities de gestión (crear formación) que correspondan.
      items: [
        { id: "formacion", label: "Formación", icon: "BookOpen", to: "/formacion" },
        { id: "formacion-historial", label: "Mi historial", icon: "Award", to: "/formacion/historial" },
      ],
    },
    {
      title: "Análisis",
      items: [
        // Reportes muestra horas (employees:manage o RLT) y/o ausencias (absences:approve).
        // El propio hub gatea los tabs visibles según capability concreta.
        { id: "reportes", label: "Reportes", icon: "TrendingUp", to: "/reportes", capabilities: ["employees:manage", "absences:approve", "time_tracking:rlt_view"] },
      ],
    },
    {
      title: "Soporte IT",
      // Módulo de Soporte IT (Help Desk con contexto de inventario) — UI-only
      // por ahora. Sin gating de módulo/capability hasta que exista el backend;
      // entonces se añadirá `module: "soporte"` y las capabilities que correspondan.
      // Las secciones de gestión (resumen, inventario, recursos, reportes) son
      // del técnico (capability `support:manage`). "Tickets" lo ve cualquier
      // empleado (autoservicio: sus propios tickets) y la base de conocimiento
      // es de consulta para todos.
      items: [
        { id: "sop-dashboard", label: "Resumen soporte", icon: "LifeBuoy", to: "/soporte", capability: "support:manage" },
        { id: "sop-tickets", label: "Tickets", icon: "Ticket", to: "/soporte/tickets" },
        { id: "sop-inventario", label: "Inventario", icon: "Package", to: "/soporte/inventario", capability: "support:manage" },
        { id: "sop-recursos", label: "Recursos IT", icon: "Cloud", to: "/soporte/recursos", capability: "support:manage" },
        { id: "sop-conocimiento", label: "Base de conocimiento", icon: "BookOpen", to: "/soporte/conocimiento" },
        { id: "sop-reportes", label: "Reportes soporte", icon: "BarChart3", to: "/soporte/reportes", capability: "support:manage" },
        // La configuración de soporte (técnicos + categorías) vive en
        // Workspace › Configuración › Soporte (sección owner/admin).
      ],
    },
    {
      title: "Workspace",
      items: [
        { id: "configuracion", label: "Configuración", icon: "Settings", to: "/configuracion" },
        ...(isAdmin
          ? [
              { id: "accesos", label: "Accesos", icon: "UserCog", to: "/ajustes/accesos" },
              { id: "modulos", label: "Módulos", icon: "ToggleRight", to: "/ajustes/modulos" },
            ]
          : []),
        // Inspección de Trabajo: estrictamente owner-only por responsabilidad legal.
        ...(isOwner
          ? [{ id: "inspeccion-tokens", label: "Inspección Trabajo", icon: "Shield", to: "/ajustes/inspeccion-tokens", module: "fichaje" }]
          : []),
        // Roles legacy — pantalla de definición de roles. Sigue role-based.
        ...(isManager ? [{ id: "roles", label: "Roles y permisos", icon: "ShieldCheck", to: "/roles" }] : []),
      ],
    },
  ];
}

/**
 * Filtra entradas del sidebar según módulos activos + capabilities
 * efectivas. Descarta grupos que quedan vacíos.
 */
function filterByAccess(groups, enabledModules, can) {
  const isOn = (item) => {
    if (item.module && !enabledModules.includes(item.module)) return false;
    // `capability` (string, single check) o `capabilities` (lista, OR) — el item
    // se muestra si el usuario tiene cualquiera de las capabilities listadas.
    if (item.capability && !can(item.capability)) return false;
    if (item.capabilities && !item.capabilities.some((c) => can(c))) return false;
    return true;
  };
  return groups
    .map((g) => ({ ...g, items: g.items.filter(isOn) }))
    .filter((g) => g.items.length > 0);
}

function NavItem({ item, active, collapsed, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={item.to}
      title={collapsed ? item.label : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        margin: "1px 0",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 13,
        textDecoration: "none",
        background: active
          ? "var(--kr-action-soft-bg)"
          : hover
            ? "var(--kr-bg-subtle)"
            : "transparent",
        color: active
          ? "var(--kr-action-soft-fg)"
          : "var(--kr-fg-primary)",
        fontWeight: active ? 600 : 400,
        justifyContent: collapsed ? "center" : "flex-start",
        transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
      }}
    >
      <Icon
        name={item.icon}
        size={16}
        style={{ color: active ? "var(--kr-action)" : "var(--kr-fg-muted)", flexShrink: 0 }}
      />
      {!collapsed && (
        <>
          <span style={{ flex: 1 }}>{item.label}</span>
          {item.count != null && item.count > 0 && (
            <span
              style={{
                background: active ? "var(--kr-bg-surface)" : "var(--kr-neutral-150)",
                color: active ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                fontSize: 11,
                padding: "1px 6px",
                borderRadius: 999,
                fontWeight: 600,
              }}
            >
              {item.count}
            </span>
          )}
        </>
      )}
    </Link>
  );
}

function WorkspaceHeader({ collapsed, workspaces, activeWorkspace, role }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const onSelect = (id) => {
    if (id && id !== activeWorkspace?.id) {
      localStorage.setItem("kronaly_workspace_id", id);
      window.location.reload();
    }
    setOpen(false);
  };
  const hasMultiple = workspaces.length > 1;
  return (
    <div
      style={{
        position: "relative",
        padding: collapsed ? "12px 12px" : "14px 14px",
        borderBottom: "1px solid var(--kr-border-subtle)",
      }}
    >
      <button
        type="button"
        onClick={() => hasMultiple && setOpen((v) => !v)}
        disabled={!hasMultiple}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "transparent",
          border: "none",
          width: "100%",
          padding: 0,
          cursor: hasMultiple ? "pointer" : "default",
          color: "inherit",
          textAlign: "left",
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: "var(--kr-neutral-900)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          <img src={logoMark} alt="Kronaly" width={20} height={20} />
        </span>
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: "var(--kr-fg-strong)",
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {activeWorkspace?.name || "Sin workspace"}
              </div>
              <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>
                {ROLE_LABEL[role] || role || "—"}
              </div>
            </div>
            {hasMultiple && (
              <Icon name="ChevronDown" size={14} style={{ color: "var(--kr-fg-muted)" }} />
            )}
          </>
        )}
      </button>
      {open && hasMultiple && !collapsed && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 8,
            right: 8,
            background: "var(--kr-bg-surface)",
            border: "1px solid var(--kr-border)",
            borderRadius: 8,
            boxShadow: "var(--kr-shadow-md)",
            padding: 6,
            marginTop: 4,
            zIndex: 30,
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
              padding: "6px 8px 2px",
            }}
          >
            Cambiar workspace
          </div>
          {workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => onSelect(w.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 8px",
                border: "none",
                background:
                  w.id === activeWorkspace?.id
                    ? "var(--kr-action-soft-bg)"
                    : "transparent",
                color:
                  w.id === activeWorkspace?.id
                    ? "var(--kr-action-soft-fg)"
                    : "var(--kr-fg-primary)",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                textAlign: "left",
              }}
            >
              <Icon
                name={w.id === activeWorkspace?.id ? "Check" : "Building2"}
                size={14}
                style={{ color: w.id === activeWorkspace?.id ? "var(--kr-action)" : "var(--kr-fg-muted)" }}
              />
              <span style={{ flex: 1 }}>{w.name}</span>
              <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                {ROLE_LABEL[w.role] || w.role}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { me, role, isManager, isAdmin, enabledModules, can } = useCurrentMembership();
  const [pendingCount, setPendingCount] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    const u = getLoggedinUser();
    if (!u?.access_token) return;
    setAuthorization(u.access_token);
    getWorkspaces()
      .then((r) => setWorkspaces(r?.data ?? r ?? []))
      .catch(() => setWorkspaces([]));
  }, []);

  // Badge de la bandeja RRHH › Solicitudes: nº de solicitudes pendientes de
  // aprobar (ausencias + horas extra) dentro del scope del aprobador. Solo para
  // aprobadores; el resto no ve el item, así que no calculamos contador.
  useEffect(() => {
    if (!isManager) { setPendingCount(null); return undefined; }
    const u = getLoggedinUser();
    if (!u?.access_token) return undefined;
    setAuthorization(u.access_token);
    let cancelled = false;
    const countOf = (r) => {
      const list = r?.data ?? r ?? [];
      return Array.isArray(list) ? list.length : 0;
    };
    Promise.all([
      getOvertimeRequests({ status: "pending" }).catch(() => ({ data: [] })),
      getAbsences({ status: "pending" }).catch(() => ({ data: [] })),
    ])
      .then(([otRes, absRes]) => {
        if (!cancelled) setPendingCount(countOf(otRes) + countOf(absRes));
      })
      .catch(() => { if (!cancelled) setPendingCount(null); });
    return () => { cancelled = true; };
  }, [isManager]);

  const isOwner = role === "owner";
  const groups = useMemo(
    () => filterByAccess(buildNav(isManager, isAdmin, can, pendingCount, isOwner), enabledModules, can),
    [isManager, isAdmin, can, pendingCount, enabledModules, isOwner],
  );

  const activeWorkspace = useMemo(() => {
    const id = localStorage.getItem("kronaly_workspace_id");
    return workspaces.find((w) => w.id === id) || workspaces[0] || null;
  }, [workspaces]);

  const isItemActive = (to) => {
    if (to === "/dashboard") return location.pathname === "/dashboard" || location.pathname === "/";
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const userName = me?.name || "Usuario";
  const { blobUrl: sidebarAvatarUrl } = useAvatarBlobUrl(me?.has_avatar ? ME_AVATAR : null, { enabled: !!me?.has_avatar });
  const userRole = ROLE_LABEL[role] || role || "—";

  // Cierre overlay al navegar (mobile).
  useEffect(() => {
    if (isMobile && mobileOpen) {
      onMobileClose?.();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  // ESC cierra el overlay mobile.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onMobileClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileOpen, onMobileClose]);

  // En mobile, el componente no se renderiza inline en el shell.
  // Cuando `mobileOpen=true`, se monta como portal con backdrop.
  if (isMobile && !mobileOpen) return null;

  const asideInner = (
    <aside
      style={{
        width: isMobile ? 280 : (collapsed ? 64 : 248),
        background: "var(--kr-bg-surface)",
        borderRight: "1px solid var(--kr-border)",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        transition: "width var(--kr-dur-base) var(--kr-ease-out)",
        height: isMobile ? "100vh" : undefined,
      }}
    >
      <WorkspaceHeader
        collapsed={collapsed}
        workspaces={workspaces}
        activeWorkspace={activeWorkspace}
        role={role}
      />

      {!collapsed && (
        <div style={{ padding: "10px 14px 0" }}>
          <img
            src={logoWordmark}
            alt="Kronaly"
            style={{ height: 16, opacity: 0.85 }}
          />
        </div>
      )}

      <nav style={{ padding: 8, flex: 1, overflow: "auto", marginTop: 4 }}>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginTop: gi === 0 ? 0 : 12 }}>
            {g.title && !collapsed && (
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--kr-fg-muted)",
                  fontWeight: 600,
                  padding: "8px 10px 4px",
                }}
              >
                {g.title}
              </div>
            )}
            {g.items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                active={isItemActive(item.to)}
                collapsed={collapsed}
                onClick={isMobile ? onMobileClose : undefined}
              />
            ))}
          </div>
        ))}
      </nav>

      <Link
        to="/configuracion"
        onClick={isMobile ? onMobileClose : undefined}
        style={{
          padding: 10,
          borderTop: "1px solid var(--kr-border-subtle)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Avatar name={userName} size={28} src={sidebarAvatarUrl || undefined} />
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: "var(--kr-fg-strong)",
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {userName}
              </div>
              <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{userRole}</div>
            </div>
            <Icon name="ChevronRight" size={14} style={{ color: "var(--kr-fg-muted)" }} />
          </>
        )}
      </Link>
    </aside>
  );

  if (!isMobile) {
    return asideInner;
  }

  // Mobile: overlay con backdrop + slide-in desde la izquierda. Portal a
  // body para que el backdrop cubra el shell entero (incluyendo Topbar).
  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onMobileClose?.(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,19,22,0.45)",
        zIndex: 1090,
        display: "flex",
      }}
    >
      {asideInner}
    </div>,
    document.body,
  );
}

export default Sidebar;
